import { useId, type ComponentPropsWithoutRef, type Ref } from 'react';

import { CheckIcon } from '@/components/icons';
import { cn } from '@/lib/utils/cn';

interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'id'> {
  label: string;
  /**
   * Masque le libellé **visuellement** seulement : il reste dans le DOM pour
   * les lecteurs d'écran. Indispensable en tête de ligne d'un tableau, où la
   * colonne n'a pas la place d'un texte mais où « Sélectionner Lina Kadiri »
   * doit rester annoncé.
   */
  hideLabel?: boolean;
  /**
   * État « partiellement cochée », pour une case qui commande un groupe dont
   * seuls certains éléments sont sélectionnés.
   *
   * Cet état n'existe pas en HTML : il n'est ni un attribut ni une valeur de
   * `checked`, seulement une **propriété du nœud DOM**. React ne le pose donc
   * pas depuis le JSX, d'où la `ref` de rappel ci-dessous.
   */
  indeterminate?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * Case à cocher stylisée.
 *
 * L'`<input>` natif est conservé et rendu transparent par-dessus le visuel,
 * plutôt que remplacé par un `<div role="checkbox">` : on garde ainsi
 * gratuitement la navigation au clavier, la barre d'espace, l'intégration au
 * formulaire et l'annonce correcte par les lecteurs d'écran.
 *
 * `peer` + `peer-checked:` permettent de refléter l'état natif en CSS, sans
 * état React à synchroniser.
 */
export const Checkbox = ({
  label,
  hideLabel = false,
  indeterminate = false,
  className,
  ref,
  disabled,
  ...rest
}: CheckboxProps) => {
  const id = useId();

  /**
   * `ref` de rappel : elle pose `indeterminate` sur le nœud à chaque rendu, et
   * transmet le nœud à la `ref` éventuellement fournie par l'appelant.
   *
   * Un `useEffect` ferait aussi l'affaire, mais après la peinture : la case
   * s'afficherait cochée l'espace d'une image avant de devenir partielle.
   */
  const applyRef = (node: HTMLInputElement | null): void => {
    if (node) node.indeterminate = indeterminate;

    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <label
      htmlFor={id}
      className={cn(
        'group inline-flex min-h-9 items-center gap-2.5 select-none',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <span className="relative grid size-5 shrink-0 place-items-center">
        <input
          id={id}
          ref={applyRef}
          type="checkbox"
          disabled={disabled}
          className="peer absolute inset-0 z-10 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          {...rest}
        />
        <span
          aria-hidden
          className={cn(
            'relative grid size-5 place-items-center rounded-[4px] border border-navy-300 bg-navy-50/60 bg-linear-to-b from-white/80 to-transparent',
            'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),0_1px_2px_rgb(15_42_95/0.06)]',
            'transition-[border-color,background-color,box-shadow] duration-200 motion-reduce:transition-none',
            !disabled && 'group-hover:border-navy-400',
            // Dégradé et liseré lumineux identiques à ceux du bouton principal.
            'peer-checked:border-accent-800/30 peer-checked:bg-accent-700 peer-checked:from-white/8',
            'peer-checked:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.14),0_2px_4px_-1px_rgb(76_29_149/0.22)]',
            // Même remplissage que l'état coché : la case partielle appartient
            // à la sélection, seul son symbole diffère.
            'peer-indeterminate:border-accent-800/30 peer-indeterminate:bg-accent-700 peer-indeterminate:from-white/8',
            !disabled &&
              'peer-checked:group-hover:bg-accent-800 peer-checked:group-hover:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.16),0_2px_5px_-1px_rgb(76_29_149/0.25)]',
            'peer-focus-visible:ring-accent-700/20 peer-focus-visible:ring-[3px] peer-focus-visible:ring-offset-2',
            'peer-checked:[&>svg]:opacity-100',
            // La coche est masquée en état partiel : le trait la remplace.
            'peer-indeterminate:[&>svg]:opacity-0 peer-indeterminate:[&>span]:opacity-100',
            disabled && 'bg-none shadow-none peer-checked:shadow-none',
          )}
        >
          <CheckIcon className="relative text-[14px] text-white opacity-0 transition-opacity duration-150 motion-reduce:transition-none" />
          <span className="absolute h-0.5 w-2.5 rounded-full bg-white opacity-0 transition-opacity duration-150 motion-reduce:transition-none" />
        </span>
      </span>

      <span
        className={cn(
          'text-navy-900 text-sm leading-snug font-medium',
          // `sr-only` plutôt qu'un retrait du DOM : la case garde un nom
          // accessible, sans quoi un lecteur d'écran n'annoncerait qu'« case à
          // cocher », sans dire laquelle.
          hideLabel && 'sr-only',
        )}
      >
        {label}
      </span>
    </label>
  );
};
